import React, { useMemo, useState } from 'react';

import * as d3 from 'd3';

import { Alert, Button, Tabs } from 'antd';
import HeatMap from 'components/module/HeatMap';
import { HeatMapProps } from 'components/module/HeatMap/HeatMap.view';
import { HeatMapChart, AssessmentsBarChart } from 'components/module/HeatMap/HeatMap.chart';
import PotentialRoi from 'components/module/PotentialRoi';
import Spinner from 'components/module/Spinner';
import useRefToPng from 'hooks/useRefToPng';
import { Diagnostic, DiagnosticGeneratedProps } from './Diagnostic.props';
import { Container, Header, UnsubmittedEmailsContainer, HiddenContainer } from './Diagnostic.style';
import UnfinishedEmail, { UnfinishedEmailProps } from 'components/module/UnfinishedEmail/UnfinishedEmail.view';

import {DownloadOutlined} from '@ant-design/icons';
import {AssessmentChart} from 'components/AssessmentChart';

import { GeneratePDF } from './GeneratePDF';
import './Diagnostic.scss';

// Helper Functions {{{
function getColorOfText(percent: number)
{
  if (percent < 64.5) {
    return '#df6f62'; // rgba(239, 73, 55, 0.7) #ef4937; //red
  }

  if (percent < 74.5) {
    return '#ffcf24'; //gold
  }

  if (percent < 80.0) {
    return '#8aaf74'; //green
  }

  return '#8aaf74';
}


const DiagnosticView = (props: DiagnosticGeneratedProps): JSX.Element => {
  const heatmapProps: HeatMapProps = {
    getColorOfText,
    survey: props.survey,
    summary: props.summary,
    ceoAnswer: props.ceoAnswer,
    unsubmittedEmails: props.unsubmittedEmails,
    enrollmentForm: props.enrollmentForm,
    unfinishedEmailProps: props.unfinishedEmail,
  };

  if (props.errors.length > 0) {
    return (
      <Alert
        message='An error has occurred'
        description={props.errors.join(', ')}
        type='error'
      />
    );
  }

  return (
    <Container>
      <Tabs defaultActiveKey='1'>
        <Tabs.TabPane tab='Heatmap' key='1'>
          {!heatmapProps.hideHeader && (
            <Header>
            <h4>Unsubmitted assessments - {heatmapProps.unsubmittedEmails.length}</h4>

            <UnsubmittedEmailsContainer>
              <ul>
                {
                  heatmapProps.unsubmittedEmails.map((m) => (
                    <UnfinishedEmail
                    key={m}
                    {...heatmapProps.unfinishedEmailProps}
                    email={m}
                    />
                  ))
                }
              </ul>
            </UnsubmittedEmailsContainer>
            </Header>
          )}
          <div style={{ margin: '0 auto', width: '90%' }}>
            <AssessmentsBarChart 
              data={Object.keys(props.summary)
                .map(key => ({
                  title: key,
                  metric: props.summary[key].categoryScore
                }))
              }
            />
          </div>

          <HeatMap {...heatmapProps} />
        </Tabs.TabPane>

        <Tabs.TabPane tab='Potential ROI' key='2'>
          <PotentialRoi
            getColorOfText={getColorOfText}
            impact={props.impact}
            companyAnnualRevenue={props.companyAnnualRevenue}
            currentNetIncome={props.currentNetIncome}
            currentNetIncomePct={props.currentNetIncomePct}
            desiredNetIncomePct={props.desiredNetIncomePct}
          />
        </Tabs.TabPane>

        <Tabs.TabPane tab="Generate PDF" key="3">
          <GeneratePDF 
            getColorOfText={getColorOfText}
            companyName={props.companyName}
            companyAnnualRevenue={props.companyAnnualRevenue}
            currentNetIncome={props.currentNetIncome}
            currentNetIncomePct={props.currentNetIncomePct}
            desiredNetIncomePct={props.desiredNetIncomePct}
            impact={props.impact}
            survey={props.survey}
            summary={props.summary}
          />
        </Tabs.TabPane>
      </Tabs>
    </Container>
  );
};

export default DiagnosticView;
